import { Controller } from "@hotwired/stimulus"
import { badgeClasses } from '@/utils/badge_classes'

/**
* Stimulus controller for displaying active form filters as preview badges
* Converts form inputs with data-filter-preview attributes into styled badges
*
* Usage:
* <form data-controller="filter-preview">
*   <input type="text" data-filter-preview="Search Term" name="search">
*   <select data-filter-preview="Category" name="category">...</select>
*   <div data-filter-preview-target="preview"></div>
* </form>
*/
export default class extends Controller {
  static targets = ["preview", "form"]
  static values = { separator: { type: String, default: ", " } }

  connect() {
    document.addEventListener('saved-form-view:load', this.handleViewLoaded.bind(this))
    this.updatePreview()
  }

  disconnect() {
    document.removeEventListener('saved-form-view:load', this.handleViewLoaded.bind(this))
  }

  formChanged() {
    this.updatePreview()
  }

  handleViewLoaded(event) {
    // Give the form a moment to populate, then update preview
    setTimeout(() => {
      this.updatePreview()
    }, 50)
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return

    const previewText = this.generatePreviewText()
    
    if (previewText) {
      this.previewTarget.innerHTML = previewText
      this.previewTarget.style.display = 'inline'
    } else {
      this.previewTarget.style.display = 'none'
    }
  }

  generatePreviewText() {
    const filters = []
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
    
    if (!form) return ''

    // Process all form elements with data-filter-preview
    const previewElements = form.querySelectorAll('[data-filter-preview]')
    
    previewElements.forEach(element => {
      const previews = this.getElementPreviews(element)
      if (previews && previews.length > 0) {
        filters.push(...previews)
      }
    })

    return filters.length > 0 ? this.createBadgesHTML(filters) : ''
  }

  getElementPreviews(element) {
    const previewLabel = element.dataset.filterPreview
    const previews = []
    
    if (element.type === 'number' && element.value) {
      previews.push({
        label: previewLabel,
        value: this.formatNumber(element.value),
        type: 'number'
      })
    }
    
    if (element.tagName === 'SELECT') {
      if (element.multiple) {
        const selected = Array.from(element.selectedOptions)
          .filter(opt => opt.value && opt.value !== '')
        
        selected.forEach(opt => {
          previews.push({
            label: previewLabel,
            value: opt.text,
            type: 'select-multiple'
          })
        })
      } else {
        const selected = element.selectedOptions[0]
        if (selected && selected.value && selected.value !== '') {
          previews.push({
            label: previewLabel,
            value: selected.text,
            type: 'select-single'
          })
        }
      }
    }
    
    if (element.type === 'checkbox' && element.checked) {
      previews.push({
        label: previewLabel,
        value: 'Yes',
        type: 'checkbox'
      })
    }

    if (element.type === 'text' && element.value.trim()) {
      previews.push({
        label: previewLabel,
        value: element.value.trim(),
        type: 'text'
      })
    }
    
    return previews
  }

  createBadgesHTML(filters) {
    // Group multi-select filters by label
    const groupedFilters = this.groupMultiSelectFilters(filters)
    
    const badges = groupedFilters.map(filter => {
      const badgeVariant = this.getBadgeVariant(filter.type)
      const icon = this.getIcon(filter.type)
      
      if (filter.type === 'select-multiple' && filter.values) {
        // Show label with count and first few values
        const valueCount = filter.values.length
        const displayText = valueCount === 1 
          ? `${filter.label}: ${filter.values[0]}`
          : valueCount <= 3
            ? `${filter.label}: ${filter.values.join(', ')}`
            : `${filter.label}: ${filter.values.slice(0, 2).join(', ')} +${valueCount - 2} more`

        return `<span class="${badgeClasses({ variant: badgeVariant, extra: 'me-1 mb-1' })}">
          ${icon}${displayText}
        </span>`
      } else {
        return `<span class="${badgeClasses({ variant: badgeVariant, extra: 'me-1 mb-1' })}">
          ${icon}${filter.label}: ${filter.value}
        </span>`
      }
    })

    return `<div class="mt-1">${badges.join('')}</div>`
  }

  groupMultiSelectFilters(filters) {
    const grouped = {}
    const result = []

    filters.forEach(filter => {
      if (filter.type === 'select-multiple') {
        if (!grouped[filter.label]) {
          grouped[filter.label] = {
            label: filter.label,
            type: 'select-multiple',
            values: []
          }
        }
        grouped[filter.label].values.push(filter.value)
      } else {
        result.push(filter)
      }
    })
    
    // Add grouped multi-select filters to result
    Object.values(grouped).forEach(groupedFilter => {
      result.push(groupedFilter)
    })
    
    return result
  }

  getBadgeVariant(type) {
    const variants = {
      'number': 'info',
      'select-single': 'primary',
      'select-multiple': 'success',
      'text': 'secondary',
      'checkbox': 'dark'
    }
    return variants[type] || 'secondary'
  }

  getIcon(type) {
    const icons = {
      'number': '<i class="fa-duotone fa-dollar-sign me-1"></i>',
      'select-single': '<i class="fa-duotone fa-filter me-1"></i>',
      'select-multiple': '<i class="fa-duotone fa-tags me-1"></i>',
      'text': '<i class="fa-duotone fa-font me-1"></i>',
      'checkbox': '<i class="fa-duotone fa-square-check me-1"></i>'
    }
    return icons[type] || ''
  }

  formatNumber(value) {
    const num = parseFloat(value)
    return num >= 1000 ? `$${(num / 1000).toFixed(0)}k` : `$${num}`
  }
}
